import { linkUpdateSchema, type Page, type SupplierProductLink } from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useCan } from "@/app/me";
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { apiFetch } from "@/lib/api";
import { Pager, SaveError, SearchInput } from "../cadastros/widgets";
import { dateTimeBR, qty, unitsOf, useDocAction, useProductDetail } from "../estoque/lib";
import { ProductPicker } from "../estoque/pickers";
import { toInputQty } from "./lib";

const KEY = ["compras", "vinculos"];

/** De/para: como cada produto do fornecedor entra no CaféGestor. */
export function VinculosPage() {
  const can = useCan();
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<SupplierProductLink | null>(null);
  const params = new URLSearchParams({ limit: "50", offset: String(offset) });
  if (q.trim()) params.set("q", q.trim());
  const list = useQuery({
    queryKey: [...KEY, params.toString()],
    queryFn: () => apiFetch<Page<SupplierProductLink>>(`/compras/vinculos?${params.toString()}`),
    placeholderData: (p) => p,
  });
  const remove = useDocAction<undefined, unknown>({
    path: () => `/compras/vinculos/${editing?.id ?? ""}`,
    method: "DELETE",
    invalidate: [KEY],
    onDone: () => setEditing(null),
  });

  return (
    <div className="space-y-6">
      <PageTitle
        title="Produtos do fornecedor (de/para)"
        description="Cada código e unidade da nota do fornecedor aponta para um produto do CaféGestor, com a conversão (ex.: 1 saca = 60 kg). Os vínculos são criados ao importar o XML e usados automaticamente nas próximas notas."
      />
      {editing && (
        <EditLink
          key={editing.id}
          link={editing}
          onClose={() => setEditing(null)}
          onDelete={() => remove.mutate(undefined)}
          deleting={remove.isPending}
          deleteError={remove.error}
        />
      )}
      <Card>
        <SearchInput
          placeholder="Buscar fornecedor, código ou produto"
          value={q}
          onChange={(v) => {
            setQ(v);
            setOffset(0);
          }}
        />
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table
              head={[
                "Fornecedor",
                "Código do fornecedor",
                "Unidade",
                "Produto",
                "Conversão",
                "Último uso",
                "",
              ]}
            >
              {list.data?.items.map((l) => (
                <tr key={l.id}>
                  <td className="px-3 py-2">{l.supplierName}</td>
                  <td className="px-3 py-2 font-mono">{l.supplierCode}</td>
                  <td className="px-3 py-2">{l.supplierUnit}</td>
                  <td className="px-3 py-2">
                    <span className="font-mono text-xs">{l.productCode}</span> {l.productName}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    1 {l.supplierUnit} = {qty(l.conversion, l.unitCode)}
                  </td>
                  <td className="px-3 py-2 text-xs">{dateTimeBR(l.lastUsedAt)}</td>
                  <td className="px-3 py-2 text-right">
                    {can("compras.entrada.lancar") && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          remove.reset();
                          setEditing(l);
                        }}
                      >
                        Alterar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
            {list.data?.items.length === 0 && (
              <p className="mt-3 text-sm text-muted-foreground">
                Nenhum vínculo ainda. Eles aparecem quando você vincula os itens de uma NF-e
                importada.
              </p>
            )}
            <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function EditLink({
  link,
  onClose,
  onDelete,
  deleting,
  deleteError,
}: {
  link: SupplierProductLink;
  onClose: () => void;
  onDelete: () => void;
  deleting: boolean;
  deleteError: unknown;
}) {
  const [productId, setProductId] = useState(link.productId);
  const [label, setLabel] = useState(`${link.productCode} — ${link.productName}`);
  const [unitId, setUnitId] = useState(link.unitId);
  const [conversion, setConversion] = useState(toInputQty(link.conversion));
  const [formError, setFormError] = useState<string | null>(null);
  const product = useProductDetail(productId);
  const units = unitsOf(product.data);
  const unit = units.find((u) => u.id === unitId) ?? units[0];
  const save = useDocAction<unknown, SupplierProductLink>({
    path: () => `/compras/vinculos/${link.id}`,
    method: "PUT",
    invalidate: [KEY],
    onDone: onClose,
    successMessage: () => "Produto do fornecedor atualizado com sucesso.",
  });
  function submit() {
    const parsed = linkUpdateSchema.safeParse({
      productId,
      unitId: unit?.id ?? "",
      conversion,
      description: link.description ?? "",
      version: link.version,
    });
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    save.mutate(parsed.data);
  }
  return (
    <Card title={`${link.supplierName}: ${link.supplierCode} (${link.supplierUnit})`}>
      <div className="grid gap-3 sm:grid-cols-4 sm:items-end">
        <div className="sm:col-span-2">
          <Field label="Produto do CaféGestor">
            <ProductPicker
              value={productId}
              label={label}
              onChange={(id, l) => {
                setProductId(id);
                setLabel(l);
                setUnitId("");
              }}
            />
          </Field>
        </div>
        <Field label="Unidade">
          <Select value={unit?.id ?? ""} onChange={(e) => setUnitId(e.target.value)}>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={`1 ${link.supplierUnit} = ? ${unit?.code ?? ""}`}>
          <Input
            inputMode="decimal"
            value={conversion}
            onChange={(e) => setConversion(e.target.value)}
          />
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={submit} disabled={save.isPending || !productId}>
          Salvar
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
        <Button variant="danger" onClick={onDelete} disabled={deleting}>
          Excluir vínculo
        </Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Alterar ou excluir o vínculo não muda entradas já lançadas; vale para as próximas notas.
      </p>
      <div className="mt-2 space-y-2">
        {formError && <Alert tone="error">{formError}</Alert>}
        <SaveError error={save.error ?? deleteError} />
      </div>
    </Card>
  );
}
